import * as THREE from 'three';

/**
 * Advanced hair shader using modified Kajiya-Kay model
 * Performance optimized for real-time rendering
 */
class HairShaderMaterial extends THREE.ShaderMaterial {
    constructor(parameters = {}) {
        const vertexShader = `
            uniform mat3 mapTransform;
            uniform mat3 alphaMapTransform;

            varying vec2 vUv;
            varying vec2 vMapUv;
            varying vec2 vAlphaMapUv;
            varying vec3 vViewPosition;
            varying vec3 vNormal;
            varying vec3 vTangent;
            varying vec3 vBitangent;

            void main() {
                vUv = uv;
                vMapUv = (mapTransform * vec3(uv, 1.0)).xy;
                vAlphaMapUv = (alphaMapTransform * vec3(uv, 1.0)).xy;

                // Calculate view vector and position
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                gl_Position = projectionMatrix * mvPosition;
                vViewPosition = -mvPosition.xyz;

                // Transform normal and compute tangent space
                vNormal = normalize(normalMatrix * normal);
                vec3 tangent = normalize(normalMatrix * vec3(1.0, 0.0, 0.0));
                vTangent = tangent;
                vBitangent = normalize(cross(vNormal, tangent));
            }
        `;

        const fragmentShader = `
            uniform sampler2D map;
            uniform sampler2D alphaMap;
            uniform sampler2D anisotropyMap;
            uniform vec3 specularColor;
            uniform float anisotropyStrength;
            uniform float alphaTest;
            uniform float roughness;
            uniform float densityMultiplier;

            varying vec2 vUv;
            varying vec2 vMapUv;
            varying vec2 vAlphaMapUv;
            varying vec3 vViewPosition;
            varying vec3 vNormal;
            varying vec3 vTangent;
            varying vec3 vBitangent;

            void main() {
                vec4 diffuseColor = texture2D(map, vMapUv);
                float alpha = texture2D(alphaMap, vAlphaMapUv).r;
                alpha = mix(alpha, 1.0, densityMultiplier * 0.5);

                if (alpha < alphaTest) discard;

                vec3 viewDir = normalize(vViewPosition);
                vec3 normal = normalize(vNormal);
                vec3 lightDir = normalize(vec3(0.0, 1.0, 0.0));
                vec3 tangent = normalize(vTangent);

                float diffuse = max(dot(normal, lightDir), 0.0) + 0.6;

                float primaryDot = dot(normalize(lightDir + viewDir), tangent);
                float primarySpec = pow(sqrt(1.0 - primaryDot * primaryDot), 2.0 / roughness);
                float secondaryDot = dot(lightDir, tangent);
                float secondarySpec = pow(sqrt(1.0 - secondaryDot * secondaryDot), 5.0 / roughness);

                vec3 specular = specularColor * (
                    primarySpec * anisotropyStrength * 2.5 +
                    secondarySpec * anisotropyStrength * 1.5
                );

                vec3 finalColor = diffuseColor.rgb * diffuse + specular;
                finalColor = mix(finalColor, finalColor * 1.2, densityMultiplier);
                finalColor = max(finalColor, diffuseColor.rgb * 0.6);

                gl_FragColor = vec4(finalColor, alpha);
            }
        `;

        super({
            vertexShader,
            fragmentShader,
            uniforms: {
                map: { value: parameters.map || new THREE.Texture() },
                mapTransform: { value: new THREE.Matrix3() },
                alphaMap: { value: parameters.alphaMap || parameters.map || new THREE.Texture() },
                alphaMapTransform: { value: new THREE.Matrix3() },
                anisotropyMap: { value: parameters.anisotropyMap || new THREE.Texture() },
                specularColor: { value: new THREE.Color(parameters.specularColor || '#3c2819') },
                anisotropyStrength: { value: parameters.anisotropyStrength || 0.9 },
                alphaTest: { value: parameters.alphaTest || 0.3 },
                roughness: { value: parameters.roughness || 3.0 },
                densityMultiplier: { value: parameters.densityMultiplier || 0.7 }
            },
            transparent: true,
            side: THREE.DoubleSide,
            depthWrite: false
        });
    }

    updateParameters(params) {
        Object.entries(params).forEach(([key, value]) => {
            if (this.uniforms[key]) {
                if (value instanceof THREE.Color) {
                    this.uniforms[key].value.copy(value);
                } else if (value instanceof THREE.Texture) {
                    this.uniforms[key].value = value;
                } else {
                    this.uniforms[key].value = value;
                }
            }
        });
        this.needsUpdate = true;
    }
}

export { HairShaderMaterial };